"use client";

import { useState, useEffect, useRef } from "react";
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Advertisement,
  AdvertisementRequestDTO,
  AdvertisementService,
} from "@/services/AdvertisementService";
import {
  XMarkIcon,
  PlayIcon,
  PhotoIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  ArrowUpTrayIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from "@heroicons/react/24/outline";

interface AdvertisementFormProps {
  advertisement?: Advertisement | null;
  mode: "create" | "edit";
  onClose: () => void;
  onSuccess: () => void;
}

// Mirrors the advertisement text column widths in the database. Submitting a longer value used to
// fail the insert while the API still answered 201, so the ad silently vanished.
const MAX_TEXT_LENGTH = 2048;
const MAX_ADTEXT_LENGTH = 1200;

const tooLongMessage = (label: string, value: string) =>
  `${label} is too long: ${value.length} characters, maximum is ${MAX_TEXT_LENGTH}`;

const SAMPLE_PATTERNS: ReadonlyArray<{ label: string; sql: string }> = [
  {
    label: "Only iOS users",
    sql: "SELECT id FROM public.user WHERE id = ? AND [ua.os] = 'ios'",
  },
  {
    label: "Only Android users",
    sql: "SELECT id FROM public.user WHERE id = ? AND [ua.os] = 'android'",
  },
  {
    label: "App version below threshold (e.g. < 4.1.0)",
    sql: "SELECT id FROM public.user WHERE id = ? AND [ua.appVersionInt] IS NOT NULL AND [ua.appVersionInt] < 40100",
  },
  {
    label: "App version in specific list",
    sql: "SELECT id FROM public.user WHERE id = ? AND [ua.appVersion] IN ('4.0.0','3.9.0')",
  },
  {
    label: "Old RN version (< 0.81.0)",
    sql: "SELECT id FROM public.user WHERE id = ? AND [ua.rnVersionInt] IS NOT NULL AND [ua.rnVersionInt] < 8100",
  },
  {
    label: "Running an OTA bundle (not embedded)",
    sql: "SELECT id FROM public.user WHERE id = ? AND [ua.otaId] IS NOT NULL AND [ua.otaId] != 'embedded'",
  },
  {
    label: "Specific device model (e.g. Pixel)",
    sql: "SELECT id FROM public.user WHERE id = ? AND [ua.deviceModel] ILIKE 'Pixel%'",
  },
  {
    label: "Show only when UA was parseable (Surfr mobile app)",
    sql: "SELECT id FROM public.user WHERE id = ? AND [ua.isParsed]",
  },
  {
    label: "Update banner: iOS users on old app version",
    sql: "SELECT id FROM public.user WHERE id = ? AND [ua.os] = 'ios' AND [ua.appVersionInt] IS NOT NULL AND [ua.appVersionInt] < 40100",
  },
];

const SAMPLE_USER_AGENT_PRESETS: ReadonlyArray<{ label: string; ua: string }> = [
  {
    label: "Latest iOS (4.0.1)",
    ua: "Surfr/4.0.1 (ios; 4.0.1; 17.4; iPhone16,2) RN/0.81.5 OTA/embedded RT/1.1.2",
  },
  {
    label: "Latest Android (4.0.1)",
    ua: "Surfr/4.0.1 (android; 4.0.1; API 34; Pixel 7 Pro) RN/0.81.5 OTA/embedded RT/1.1.2",
  },
  {
    label: "Old iOS (3.9.0)",
    ua: "Surfr/3.9.0 (ios; 3.9.0; 16.6; iPhone13,2) RN/0.74.0 OTA/embedded RT/1.0.0",
  },
  {
    label: "Old Android (3.9.0)",
    ua: "Surfr/3.9.0 (android; 3.9.0; API 31; SM-G991B) RN/0.74.0 OTA/embedded RT/1.0.0",
  },
  {
    label: "Browser / unparseable",
    ua: "Mozilla/5.0 (X11; Linux x86_64) Chrome/124.0",
  },
];

const PLACEHOLDER_ROWS: ReadonlyArray<{ name: string; type: string; example: string }> = [
  { name: "[ua.appVersion]", type: "string", example: "'4.0.1'" },
  { name: "[ua.appVersionInt]", type: "int", example: "40010 (for 4.0.10)" },
  { name: "[ua.buildVersion]", type: "string", example: "'4.0.1'" },
  { name: "[ua.os]", type: "string", example: "'android' / 'ios'" },
  { name: "[ua.osDetail]", type: "string", example: "'API 34' / '17.4'" },
  { name: "[ua.deviceModel]", type: "string", example: "'Pixel 7 Pro'" },
  { name: "[ua.rnVersion]", type: "string", example: "'0.81.5'" },
  { name: "[ua.rnVersionInt]", type: "int", example: "8105" },
  { name: "[ua.otaId]", type: "string", example: "'embedded' / first 8 of update id" },
  { name: "[ua.runtimeVersion]", type: "string", example: "'1.1.2'" },
  { name: "[ua.isParsed]", type: "bool", example: "true / false" },
];

export default function AdvertisementForm({
  advertisement,
  mode,
  onClose,
  onSuccess,
}: AdvertisementFormProps) {
  const [formData, setFormData] = useState<AdvertisementRequestDTO>({
    adid: 0,
    type: "ad_photo",
    resource: "",
    shareurl: "",
    buttontext: "",
    buttonurl: "",
    adtext: "",
    qualifyingquery: "select id from public.user where id = ?",
    profilepage: null,
    sessionpage: null,
    profilelistposition: null,
    sessionlistposition: null,
    active: false,
    internalurl: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [queryTestResult, setQueryTestResult] = useState<{
    valid: boolean;
    message: string;
    substitutedQuery?: string;
  } | null>(null);
  const [testingQuery, setTestingQuery] = useState(false);
  const [sampleUserAgent, setSampleUserAgent] = useState<string>("");
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const qualifyingQueryRef = useRef<HTMLTextAreaElement | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [showCropPreview, setShowCropPreview] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (advertisement && mode === "edit") {
      setFormData({
        adid: advertisement.adid,
        type: advertisement.type,
        resource: advertisement.resource,
        shareurl: advertisement.shareurl,
        buttontext: advertisement.buttontext,
        buttonurl: advertisement.buttonurl,
        adtext: advertisement.adtext,
        qualifyingquery: advertisement.qualifyingquery,
        profilepage: advertisement.profilepage,
        sessionpage: advertisement.sessionpage,
        profilelistposition: advertisement.profilelistposition,
        sessionlistposition: advertisement.sessionlistposition,
        active: advertisement.active,
        internalurl: advertisement.internalurl,
      });
    } else if (mode === "create") {
      // Generate next ad ID for new advertisements
      const nextAdId = Math.floor(Math.random() * 1000) + 200;
      setFormData((prev) => ({ ...prev, adid: nextAdId }));
    }
  }, [advertisement, mode]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.adid || formData.adid <= 0) {
      newErrors.adid = "Ad ID is required and must be positive";
    }

    if (!formData.resource.trim()) {
      newErrors.resource = "Resource URL is required";
    } else if (
      !formData.resource.startsWith("http") &&
      !formData.resource.startsWith("/")
    ) {
      newErrors.resource = "Resource must be a valid URL";
    } else if (formData.resource.length > MAX_TEXT_LENGTH) {
      newErrors.resource = tooLongMessage("Resource URL", formData.resource);
    }

    if (!formData.shareurl.trim()) {
      newErrors.shareurl = "Share URL is required";
    } else if (formData.shareurl.length > MAX_TEXT_LENGTH) {
      newErrors.shareurl = tooLongMessage("Share URL", formData.shareurl);
    }

    if (!formData.buttontext.trim()) {
      newErrors.buttontext = "Button text is required";
    } else if (formData.buttontext.length > MAX_TEXT_LENGTH) {
      newErrors.buttontext = tooLongMessage("Button text", formData.buttontext);
    }

    if (!formData.buttonurl.trim()) {
      newErrors.buttonurl = "Button URL is required";
    } else if (formData.buttonurl.length > MAX_TEXT_LENGTH) {
      newErrors.buttonurl = tooLongMessage("Button URL", formData.buttonurl);
    }

    if (!formData.adtext.trim()) {
      newErrors.adtext = "Ad text is required";
    } else if (formData.adtext.length > MAX_ADTEXT_LENGTH) {
      newErrors.adtext = `Ad text must be less than ${MAX_ADTEXT_LENGTH} characters`;
    }

    if (!formData.qualifyingquery.trim()) {
      newErrors.qualifyingquery = "Qualifying query is required";
    }

    if (formData.profilepage == null && formData.sessionpage == null) {
      newErrors.pages = "Advertisement must appear on at least one feed";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (mode === "edit" && advertisement) {
        await AdvertisementService.updateAdvertisement(advertisement.id, formData);
      } else {
        await AdvertisementService.createAdvertisement(formData);
      }
      onSuccess();
    } catch (error: any) {
      // Prefer the backend's message — it names the offending field on a 400.
      setErrors({
        submit:
          error?.response?.data?.error ||
          error?.message ||
          "Failed to save advertisement",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestQuery = async () => {
    setTestingQuery(true);
    setQueryTestResult(null);

    try {
      const result = sampleUserAgent
        ? await AdvertisementService.previewQualifyingQuery(
            formData.qualifyingquery,
            sampleUserAgent,
            265
          )
        : await AdvertisementService.testQualifyingQuery(
            formData.qualifyingquery,
            265
          );

      const valid = !!result.valid;
      setQueryTestResult({
        valid,
        message:
          result.message ||
          (valid
            ? "Query is valid and returned results"
            : result.error || "Query failed or returned no results"),
        substitutedQuery: (result as { substitutedQuery?: string }).substitutedQuery,
      });
    } catch (error: any) {
      setQueryTestResult({
        valid: false,
        message:
          error?.response?.data?.error ||
          error?.message ||
          "Failed to test query",
      });
    } finally {
      setTestingQuery(false);
    }
  };

  const insertAtCursor = (snippet: string) => {
    const el = qualifyingQueryRef.current;
    const current = formData.qualifyingquery || "";
    if (!el) {
      setFormData({ ...formData, qualifyingquery: current + snippet });
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + snippet + current.slice(end);
    setFormData({ ...formData, qualifyingquery: next });
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  };


  const handleResourceTypeChange = (type: "ad_photo" | "ad_vid") => {
    setFormData({ ...formData, type });

    // Auto-adjust resource URL based on type if it's a YouTube URL
    if (type === "ad_vid" && formData.resource.includes("youtube.com")) {
      const videoId = formData.resource.match(/(?:v=|\/embed\/|youtu\.be\/)([^&\n?#]+)/)?.[1];
      if (videoId) {
        setFormData(prev => ({
          ...prev,
          type,
          resource: `https://www.youtube.com/embed/${videoId}`
        }));
      }
    }
  };

  // Helper to create a center crop with 16:9 aspect ratio
  function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number = 16 / 9
  ) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  }

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const crop = centerAspectCrop(width, height);
    setCrop(crop);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!acceptedTypes.includes(file.type)) {
      setErrors({ resource: 'Please upload a valid image file (JPEG, PNG, GIF, WebP, SVG)' });
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setErrors({ resource: 'Image size must be less than 10MB' });
      return;
    }

    setSelectedFile(file);
    setErrors({});

    // Show preview with crop tool
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
      setShowCropPreview(true);
    };
    reader.readAsDataURL(file);
  };

  // Convert WebP or other formats to PNG/JPEG on client side
  const convertImageToSupportedFormat = async (file: File): Promise<File> => {
    // If it's already PNG or JPEG, return as is
    if (file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg') {
      return file;
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          if (blob) {
            // Convert to PNG format
            const convertedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.png'), {
              type: 'image/png'
            });
            resolve(convertedFile);
          } else {
            reject(new Error('Failed to convert image'));
          }
        }, 'image/png', 0.95);
      };

      img.onerror = () => reject(new Error('Failed to load image'));

      // Create object URL from file
      const url = URL.createObjectURL(file);
      img.src = url;
    });
  };

  const handleCropConfirm = async () => {
    if (!selectedFile || !completedCrop) return;

    setUploadingImage(true);
    setShowCropPreview(false);

    try {
      // Store image dimensions before any conversion
      let naturalWidth = imageRef.current?.naturalWidth || 0;
      let naturalHeight = imageRef.current?.naturalHeight || 0;
      let displayedWidth = imageRef.current?.width || 0;
      let displayedHeight = imageRef.current?.height || 0;

      // If we couldn't get dimensions from the ref, try to get them from the image
      if (naturalWidth === 0 || naturalHeight === 0) {
        // Load the image to get dimensions
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imagePreview || URL.createObjectURL(selectedFile);
        });
        naturalWidth = img.width;
        naturalHeight = img.height;
        // For display dimensions, use the same as natural if we don't have the ref
        if (displayedWidth === 0) displayedWidth = naturalWidth;
        if (displayedHeight === 0) displayedHeight = naturalHeight;
      }

      // Convert WebP or other formats to supported format if needed
      let fileToUpload = selectedFile;
      if (selectedFile.type === 'image/webp' || selectedFile.type === 'image/svg+xml') {
        try {
          fileToUpload = await convertImageToSupportedFormat(selectedFile);
          console.log(`Converted ${selectedFile.type} to ${fileToUpload.type}`);
        } catch (conversionError) {
          console.error('Failed to convert image:', conversionError);
          // Continue with original file and let server handle the error
        }
      }

      // Calculate scale factors
      const scaleX = naturalWidth / displayedWidth;
      const scaleY = naturalHeight / displayedHeight;

      // Scale the crop coordinates to match the original image dimensions
      const scaledCrop = {
        x: Math.round(completedCrop.x * scaleX),
        y: Math.round(completedCrop.y * scaleY),
        width: Math.round(completedCrop.width * scaleX),
        height: Math.round(completedCrop.height * scaleY)
      };

      console.log('Image dimensions - Natural:', naturalWidth, 'x', naturalHeight,
                  'Displayed:', displayedWidth, 'x', displayedHeight);
      console.log('Crop - Original:', completedCrop, 'Scaled:', scaledCrop);

      // Upload with properly scaled crop coordinates
      const optimizedUrl = await AdvertisementService.uploadAndOptimizeImage(
        fileToUpload,
        scaledCrop
      );

      setFormData(prev => ({
        ...prev,
        resource: optimizedUrl
      }));

      setImagePreview(optimizedUrl);
      setSelectedFile(null);
      setCrop(undefined);
      setCompletedCrop(undefined);
    } catch (error: any) {
      console.error('Image upload error:', error);
      const errorMessage = error.message || 'Failed to upload image';

      // Provide more specific error messages
      if (errorMessage.includes('naturalWidth')) {
        setErrors({ resource: 'Failed to process image dimensions. Please try uploading again.' });
      } else if (errorMessage.includes('WebP')) {
        setErrors({ resource: 'WebP format conversion failed. Please try a different image format.' });
      } else {
        setErrors({ resource: errorMessage });
      }

      setImagePreview(null);
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCropCancel = () => {
    setShowCropPreview(false);
    setImagePreview(null);
    setSelectedFile(null);
    setCrop(undefined);
    setCompletedCrop(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            {mode === "edit" ? "Edit Advertisement" : "Create New Advertisement"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-6">
            {/* Basic Information */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4">
                Basic Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ad ID *
                  </label>
                  <input
                    type="number"
                    value={formData.adid}
                    onChange={(e) =>
                      setFormData({ ...formData, adid: parseInt(e.target.value) || 0 })
                    }
                    className={`w-full px-3 py-2 border rounded-md ${
                      errors.adid ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.adid && (
                    <p className="mt-1 text-sm text-red-600">{errors.adid}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="ad_photo"
                        checked={formData.type === "ad_photo"}
                        onChange={(e) =>
                          handleResourceTypeChange(e.target.value as "ad_photo")
                        }
                        className="mr-2"
                      />
                      <PhotoIcon className="h-5 w-5 mr-1" />
                      Photo
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="ad_vid"
                        checked={formData.type === "ad_vid"}
                        onChange={(e) =>
                          handleResourceTypeChange(e.target.value as "ad_vid")
                        }
                        className="mr-2"
                      />
                      <PlayIcon className="h-5 w-5 mr-1" />
                      Video
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Media Resources */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4">
                Media & Links
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Resource URL *
                    <span className="ml-2 text-xs text-gray-500">
                      ({formData.type === "ad_vid" ? "Video embed URL" : "Image URL"})
                    </span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.resource}
                      maxLength={MAX_TEXT_LENGTH}
                      onChange={(e) => {
                        setFormData({ ...formData, resource: e.target.value });
                        if (formData.type === "ad_photo" && e.target.value) {
                          setImagePreview(e.target.value);
                        }
                      }}
                      placeholder={
                        formData.type === "ad_vid"
                          ? "https://www.youtube.com/embed/VIDEO_ID"
                          : "https://example.com/image.jpg or upload below"
                      }
                      className={`flex-1 px-3 py-2 border rounded-md ${
                        errors.resource ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {formData.type === "ad_photo" && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                      >
                        {uploadingImage ? (
                          <>
                            <CloudArrowUpIcon className="h-5 w-5 animate-pulse" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <ArrowUpTrayIcon className="h-5 w-5" />
                            Upload
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  {errors.resource && (
                    <p className="mt-1 text-sm text-red-600">{errors.resource}</p>
                  )}

                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                    onChange={handleImageUpload}
                    className="hidden"
                  />

                  {/* Upload hints */}
                  {formData.type === "ad_photo" && (
                    <p className="mt-1 text-xs text-gray-500">
                      Supported formats: JPEG, PNG, GIF, WebP (max 10MB). WebP images will be converted to PNG. All images resized to 1280×720.
                    </p>
                  )}

                  {/* Preview */}
                  {(imagePreview || formData.resource) && formData.type === "ad_photo" && (
                    <div className="mt-2">
                      <img
                        src={imagePreview || formData.resource}
                        alt="Preview"
                        className="h-32 rounded border border-gray-200 object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    </div>
                  )}

                  {formData.type === "ad_vid" && formData.resource && (
                    <div className="mt-2 text-sm text-gray-600">
                      Video preview will be shown in the preview dialog
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Share URL *
                  </label>
                  <input
                    type="text"
                    value={formData.shareurl}
                    maxLength={MAX_TEXT_LENGTH}
                    onChange={(e) =>
                      setFormData({ ...formData, shareurl: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded-md ${
                      errors.shareurl ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.shareurl && (
                    <p className="mt-1 text-sm text-red-600">{errors.shareurl}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4">Content</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ad Text *
                    <span className="ml-2 text-xs text-gray-500">
                      ({formData.adtext.length}/1200 characters)
                    </span>
                  </label>
                  <textarea
                    value={formData.adtext}
                    maxLength={MAX_ADTEXT_LENGTH}
                    onChange={(e) =>
                      setFormData({ ...formData, adtext: e.target.value })
                    }
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md ${
                      errors.adtext ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter the advertisement text that will be displayed..."
                  />
                  {errors.adtext && (
                    <p className="mt-1 text-sm text-red-600">{errors.adtext}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    You can use [name] and [offerurl] placeholders for personalization
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Button Text *
                    </label>
                    <input
                      type="text"
                      value={formData.buttontext}
                      maxLength={MAX_TEXT_LENGTH}
                      onChange={(e) =>
                        setFormData({ ...formData, buttontext: e.target.value })
                      }
                      placeholder="e.g., Learn More"
                      className={`w-full px-3 py-2 border rounded-md ${
                        errors.buttontext ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.buttontext && (
                      <p className="mt-1 text-sm text-red-600">{errors.buttontext}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Button URL *
                    </label>
                    <input
                      type="text"
                      value={formData.buttonurl}
                      maxLength={MAX_TEXT_LENGTH}
                      onChange={(e) =>
                        setFormData({ ...formData, buttonurl: e.target.value })
                      }
                      className={`w-full px-3 py-2 border rounded-md ${
                        errors.buttonurl ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.buttonurl && (
                      <p className="mt-1 text-sm text-red-600">{errors.buttonurl}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Targeting */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4">
                Targeting & Placement
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Qualifying Query *
                    <button
                      type="button"
                      onClick={handleTestQuery}
                      disabled={testingQuery}
                      className="ml-2 text-xs text-indigo-600 hover:text-indigo-500"
                    >
                      {testingQuery ? "Testing..." : "Test Query"}
                    </button>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    SQL that decides whether this ad is shown to a given user.
                    The first <code className="bg-gray-100 px-1 rounded">?</code> is bound to <code className="bg-gray-100 px-1 rounded">user.id</code>.
                    <code className="bg-gray-100 px-1 rounded">[ua.*]</code> placeholders are replaced with safe SQL literals
                    from the request&apos;s <code className="bg-gray-100 px-1 rounded">User-Agent</code> header before execution.
                    Missing UA fields become <code className="bg-gray-100 px-1 rounded">NULL</code>, so ads that depend on them won&apos;t show by default.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <select
                      onChange={(e) => {
                        const idx = parseInt(e.target.value, 10);
                        if (!isNaN(idx) && SAMPLE_PATTERNS[idx]) {
                          insertAtCursor(SAMPLE_PATTERNS[idx].sql);
                        }
                        e.target.value = "";
                      }}
                      defaultValue=""
                      className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white"
                    >
                      <option value="" disabled>
                        Insert sample pattern…
                      </option>
                      {SAMPLE_PATTERNS.map((p, i) => (
                        <option key={p.label} value={i}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowPlaceholders((s) => !s)}
                      className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                    >
                      {showPlaceholders ? "Hide" : "Show"} available [ua.*] placeholders
                    </button>
                  </div>
                  {showPlaceholders && (
                    <div className="mb-2 border border-gray-200 rounded-md bg-gray-50 p-3 text-xs">
                      <p className="mb-2 text-gray-700">
                        Use any of these inside the SQL. String placeholders accept an optional
                        <code className="mx-1 bg-white px-1 rounded">:lower</code> or
                        <code className="mx-1 bg-white px-1 rounded">:upper</code> modifier,
                        e.g. <code className="bg-white px-1 rounded">[ua.os:upper]</code>.
                      </p>
                      <table className="w-full text-left">
                        <thead>
                          <tr className="text-gray-500">
                            <th className="py-1 pr-3">Placeholder</th>
                            <th className="py-1 pr-3">Type</th>
                            <th className="py-1">Example value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {PLACEHOLDER_ROWS.map((row) => (
                            <tr key={row.name} className="border-t border-gray-200">
                              <td className="py-1 pr-3 font-mono">{row.name}</td>
                              <td className="py-1 pr-3">{row.type}</td>
                              <td className="py-1 font-mono">{row.example}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <textarea
                    ref={qualifyingQueryRef}
                    value={formData.qualifyingquery}
                    onChange={(e) =>
                      setFormData({ ...formData, qualifyingquery: e.target.value })
                    }
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-md font-mono text-sm ${
                      errors.qualifyingquery ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="SELECT id FROM public.user WHERE id = ?"
                  />
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Sample User-Agent (used by Test Query)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <select
                        onChange={(e) => {
                          if (e.target.value !== "__custom__") {
                            setSampleUserAgent(e.target.value);
                          }
                        }}
                        value={
                          SAMPLE_USER_AGENT_PRESETS.find((p) => p.ua === sampleUserAgent)
                            ? sampleUserAgent
                            : "__custom__"
                        }
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white"
                      >
                        <option value="__custom__">Custom (use input)</option>
                        {SAMPLE_USER_AGENT_PRESETS.map((p) => (
                          <option key={p.label} value={p.ua}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={sampleUserAgent}
                        onChange={(e) => setSampleUserAgent(e.target.value)}
                        placeholder="Surfr/4.0.1 (android; 4.0.1; API 34; Pixel 7 Pro) RN/0.81.5 OTA/embedded RT/1.1.2"
                        className="flex-1 min-w-[20rem] px-2 py-1 text-xs border border-gray-300 rounded-md font-mono"
                      />
                      {sampleUserAgent && (
                        <button
                          type="button"
                          onClick={() => setSampleUserAgent("")}
                          className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Leave blank to run the regular test (no UA substitution). With a UA filled in, Test Query uses the admin preview endpoint and returns the substituted SQL.
                    </p>
                  </div>
                  {errors.qualifyingquery && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.qualifyingquery}
                    </p>
                  )}
                  {queryTestResult && (
                    <div
                      className={`mt-2 p-2 rounded text-sm ${
                        queryTestResult.valid
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      <div className="flex items-center">
                        {queryTestResult.valid ? (
                          <CheckCircleIcon className="h-5 w-5 mr-2" />
                        ) : (
                          <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                        )}
                        {queryTestResult.message}
                      </div>
                      {queryTestResult.substitutedQuery && (
                        <pre className="mt-2 text-xs whitespace-pre-wrap break-words bg-white/50 p-2 rounded font-mono">
                          {queryTestResult.substitutedQuery}
                        </pre>
                      )}
                    </div>
                  )}
                </div>

                {/* Profile Feed Placement */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      checked={formData.profilepage != null}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, profilepage: 0, profilelistposition: 0 });
                        } else {
                          setFormData({ ...formData, profilepage: null, profilelistposition: null });
                        }
                      }}
                      className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      Show on Profile feed
                    </span>
                    {formData.profilepage != null && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Active
                      </span>
                    )}
                  </label>
                  {formData.profilepage != null && (
                    <div className="grid grid-cols-2 gap-4 ml-6">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Page number
                        </label>
                        <input
                          type="number"
                          value={formData.profilepage ?? 0}
                          onChange={(e) =>
                            setFormData({ ...formData, profilepage: parseInt(e.target.value) || 0 })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          List position
                        </label>
                        <input
                          type="number"
                          value={formData.profilelistposition ?? 0}
                          onChange={(e) =>
                            setFormData({ ...formData, profilelistposition: parseInt(e.target.value) || 0 })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          min="0"
                        />
                      </div>
                      <p className="col-span-2 text-xs text-gray-500">
                        Page 0 = first load, Page 1 = second load (scroll), etc. Position 0 = before first item.
                      </p>
                    </div>
                  )}
                </div>

                {/* Session Feed Placement */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      checked={formData.sessionpage != null}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, sessionpage: 0, sessionlistposition: 0 });
                        } else {
                          setFormData({ ...formData, sessionpage: null, sessionlistposition: null });
                        }
                      }}
                      className="mr-2 h-4 w-4 text-green-600 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      Show on Session feed
                    </span>
                    {formData.sessionpage != null && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </label>
                  {formData.sessionpage != null && (
                    <div className="grid grid-cols-2 gap-4 ml-6">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Page number
                        </label>
                        <input
                          type="number"
                          value={formData.sessionpage ?? 0}
                          onChange={(e) =>
                            setFormData({ ...formData, sessionpage: parseInt(e.target.value) || 0 })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          List position
                        </label>
                        <input
                          type="number"
                          value={formData.sessionlistposition ?? 0}
                          onChange={(e) =>
                            setFormData({ ...formData, sessionlistposition: parseInt(e.target.value) || 0 })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          min="0"
                        />
                      </div>
                      <p className="col-span-2 text-xs text-gray-500">
                        Page 0 = first load, Page 1 = second load (scroll), etc. Position 0 = before first item.
                      </p>
                    </div>
                  )}
                </div>

                {errors.pages && (
                  <p className="text-sm text-red-600">{errors.pages}</p>
                )}
              </div>
            </div>

            {/* Settings */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4">Settings</h4>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) =>
                      setFormData({ ...formData, active: e.target.checked })
                    }
                    className="mr-2 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Active (Advertisement will be shown to users)
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.internalurl}
                    onChange={(e) =>
                      setFormData({ ...formData, internalurl: e.target.checked })
                    }
                    className="mr-2 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Internal URL (Open within the app)
                  </span>
                </label>
              </div>
            </div>

            {/* Error message */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {errors.submit}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Saving..." : mode === "edit" ? "Update" : "Create"}
          </button>
        </div>
      </div>

      {/* Image Crop Modal */}
      {showCropPreview && imagePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Adjust Image Position
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Drag to reposition the image. The highlighted area shows what will be visible in the 16:9 advertisement.
              </p>
            </div>

            {/* Crop Area */}
            <div className="flex-1 overflow-auto p-6 bg-gray-50">
              <div className="flex flex-col items-center">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={16 / 9}
                  className="max-w-full"
                >
                  <img
                    ref={imageRef}
                    alt="Crop preview"
                    src={imagePreview}
                    onLoad={onImageLoad}
                    className="max-w-full max-h-[60vh]"
                  />
                </ReactCrop>

                {/* Preview Info */}
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600">
                    Final size: <span className="font-medium">1280 × 720 pixels</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    The image will be automatically resized to this dimension while maintaining quality
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <ArrowsPointingOutIcon className="h-4 w-4" />
                <span>Drag to adjust position</span>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCropCancel}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCropConfirm}
                  disabled={!completedCrop || uploadingImage}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {uploadingImage ? (
                    <>
                      <CloudArrowUpIcon className="h-4 w-4 animate-pulse" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4" />
                      Confirm & Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}